import { dateMs, isEmptyValue } from './calc';
import type { DateValue, PropertyDef, RowData, SortRule } from './db-types';
import type { ValueOf } from './filter';

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

function sortKey(prop: PropertyDef, v: unknown, memberName: (id: string) => string): number | string {
  switch (prop.type) {
    case 'select':
    case 'status': {
      const i = prop.options?.findIndex((o) => o.id === v) ?? -1;
      return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    }
    case 'multi_select': {
      const ids = v as string[];
      const idx = ids.map((id) => prop.options?.findIndex((o) => o.id === id) ?? -1).filter((i) => i >= 0);
      return idx.length ? Math.min(...idx) : Number.MAX_SAFE_INTEGER;
    }
    case 'checkbox':
      return v ? 1 : 0;
    case 'date':
      return dateMs(v as DateValue) ?? 0;
    case 'person':
      return (v as string[]).map(memberName).join(', ');
    case 'created_by':
    case 'last_edited_by':
      return memberName(v as string);
    default:
      if (typeof v === 'number') return v;
      if (v instanceof Date) return v.getTime();
      if (typeof v === 'boolean') return v ? 1 : 0;
      if (Array.isArray(v)) return v.map(String).join(', ');
      return String(v ?? '');
  }
}

/**
 * Sorts rows by the rules in order (later rules break ties). Empty values sort last in both
 * directions; remaining ties keep the manual order.
 */
export function sortRows(rows: RowData[], sorts: SortRule[], props: PropertyDef[], valueOf: ValueOf, memberName: (id: string) => string = (id) => id): RowData[] {
  const active = sorts.map((s) => ({ s, prop: props.find((p) => p.id === s.propId) })).filter((x): x is { s: SortRule; prop: PropertyDef } => !!x.prop);
  if (!active.length) return rows;
  const keyed = rows.map((row, index) => ({ row, index, keys: active.map(({ prop }) => {
    const v = valueOf(row, prop);
    return { empty: prop.type !== 'checkbox' && isEmptyValue(v), key: sortKey(prop, v, memberName) };
  }) }));
  keyed.sort((a, b) => {
    for (let i = 0; i < active.length; i++) {
      const ka = a.keys[i]!;
      const kb = b.keys[i]!;
      if (ka.empty !== kb.empty) return ka.empty ? 1 : -1;
      if (ka.empty) continue;
      const c = typeof ka.key === 'number' && typeof kb.key === 'number' ? ka.key - kb.key : collator.compare(String(ka.key), String(kb.key));
      if (c !== 0) return active[i]!.s.dir === 'asc' ? c : -c;
    }
    return a.index - b.index;
  });
  return keyed.map((k) => k.row);
}

/** Moves the sort rule at `from` to `to` (drag to reorder in the sort menu). */
export function moveSort(sorts: SortRule[], from: number, to: number): SortRule[] {
  const out = [...sorts];
  const [s] = out.splice(from, 1);
  if (s) out.splice(Math.max(0, Math.min(to, out.length)), 0, s);
  return out;
}
